import React from "react";
import {
  Card,
  Grid,
  Typography,
  Theme,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  CheckCircle as OnTrackIcon,
  Warning as DelayedIcon,
  Assessment as TCQIIcon,
  Speed as SpeedIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as PerformanceIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material";
import { DeliveryPerformanceMetric } from "../types";
import { KpiCard } from "./KpiCard";

interface DeliveryPerformanceProps {
  metrics: DeliveryPerformanceMetric[];
  isLoading?: boolean;
  isError?: boolean;
  error?: string;
}

const getIcon = (label: string) => {
  if (label.includes("track")) return <OnTrackIcon fontSize="large" />;
  if (label.includes("Delayed")) return <DelayedIcon fontSize="large" />;
  if (label.includes("TCQI")) return <TCQIIcon fontSize="large" />;
  if (label.includes("approval")) return <SpeedIcon fontSize="large" />;
  if (label.includes("Reg. Dates")) return <CalendarIcon fontSize="large" />;
  if (label.includes("Performance"))
    return <PerformanceIcon fontSize="large" />;
  return <OnTrackIcon fontSize="large" />;
};

const getIconColor = (label: string, theme: Theme) => {
  if (label.includes("track")) return theme.palette.success.main;
  if (label.includes("Delayed")) return theme.palette.error.main;
  if (label.includes("TCQI")) return theme.palette.info.main;
  if (label.includes("approval")) return theme.palette.warning.main;
  if (label.includes("Reg. Dates")) return theme.palette.error.main;
  if (label.includes("Performance")) return theme.palette.success.main;
  return theme.palette.primary.main;
};

export const DeliveryPerformance: React.FC<DeliveryPerformanceProps> = ({
  metrics,
  isLoading = false,
  isError = false,
  error,
}) => {
  const theme = useTheme();

  return (
    <Card variant="outlined" sx={{ p: 0 }}>
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "action.hover",
        }}
      >
        <Typography variant="subtitle2">Delivery Performance</Typography>
      </Box>
      <Box sx={{ p: 2 }}>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 200,
            }}
          >
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Alert severity="error">
            {error || "Failed to load delivery performance"}
          </Alert>
        ) : metrics.length > 0 ? (
          <Grid container spacing={2}>
            {metrics.map((metric, index) => (
              <Grid size={{ xs: 12, sm: 6 }} key={index}>
                <KpiCard
                  label={metric.label}
                  value={metric.value}
                  icon={getIcon(metric.label)}
                  iconColor={getIconColor(metric.label, theme)}
                  subValue={metric.subValue}
                />
              </Grid>
            ))}
          </Grid>
        ) : null}
      </Box>
    </Card>
  );
};
